import {
  SkeletonMatchIncidentsComponent,
  SkeletonMatchSummaryComponent,
} from '@/app/components/shared/skeleton';
import { useWsMatchIncidents } from '@/app/core/websocket/hooks';
import { FootballMatchesService } from '@/app/pages/sports/football/home/services';
import { ThemeService } from '@/app/services/theme.service';
import { TranslationService } from '@/app/services/translation.service';
import { environment } from '@/environments/environment';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  IncidentsComponent,
  LivetrackerComponent,
} from '../../../../components';
import { CompetitionMatchSummary, MatchIncidents } from '../../../../models';
import { MatchIncidentsService } from '../../../../services';
import { EGamePeriod } from '@/app/pages/sports/football/enums';

@Component({
  selector: 'app-summary',
  standalone: true,
  imports: [
    LivetrackerComponent,
    IncidentsComponent,
    SkeletonMatchSummaryComponent,
    SkeletonMatchIncidentsComponent,
    TranslateModule,
    RouterModule,
  ],
  templateUrl: './summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryComponent implements OnDestroy {
  private activeRoute = inject(ActivatedRoute);
  private translateService = inject(TranslationService);
  private translateServiceCore = inject(TranslateService);
  private matchIncidentsService = inject(MatchIncidentsService);
  private themeService = inject(ThemeService);

  private refreshIntervalId!: ReturnType<typeof setInterval>;
  private soccerMatchesService = inject(FootballMatchesService);

  wsIncidents = useWsMatchIncidents();

  wsDataMatch = effect(
    () => {
      this.soccerMatchesService.setWsIncidents(this.wsIncidents.data());
    },
    { allowSignalWrites: true }
  );

  competition = input.required<CompetitionMatchSummary | null>();
  gamePeriod = input<EGamePeriod>();

  youtuveLink = computed(() => {
    return this.competition()?.YoutubeUrl
      ? this.competition()?.YoutubeUrl
      : 'https://www.youtube.com';
  });

  matchId = signal<string>('');
  tsExternalMatchId = input.required<string>();

  baseMatchIncidents = computed(() =>
    this.matchIncidentsService.matchIncidents()
  );
  matchIncidents = computed((): MatchIncidents => {
    const ws = this.soccerMatchesService.wsIncidents()[this.matchId()];

    const newMatchIncidents = ws
      ? {
          ...this.baseMatchIncidents(),
          FirstHalfIncidents:
            this.gamePeriod() === EGamePeriod.FIRST_HALF
              ? [
                  ...ws.incidents.FirstHalfIncidents.reverse(),
                  ...ws.incidents.SecondHalfIncidents.reverse(),
                ]
              : [...ws.incidents.FirstHalfIncidents.reverse()],
          SecondHalfIncidents:
            this.gamePeriod() === EGamePeriod.FIRST_HALF
              ? []
              : [...ws.incidents.SecondHalfIncidents.reverse()],
          OvertimeIncidents: ws.incidents.OvertimeIncidents.reverse(),
          PenaltyShootOutIncidents:
            ws.incidents.PenaltyShootOutIncidents.reverse(),
        }
      : this.baseMatchIncidents();

    this.baseMatchIncidents = signal(newMatchIncidents);

    return newMatchIncidents;
  });

  isLoading = computed(() => {
    return this.matchIncidentsService.isLoadingMatchIncidents();
  });

  getYoutubeLogo = computed(() => {
    const isDarkMode = this.themeService.currentTheme() === 'dark';

    return `${environment.urlIMG}youtube-logo${isDarkMode ? '-dark' : ''}.png`;
  });

  constructor() {
    this.activeRoute.paramMap.subscribe((params) => {
      this.matchId.set(params.get('matchId')!);
    });

    if (this.matchId())
      this.matchIncidentsService.getMatchIncidents(this.matchId()!);
    this.refreshIntervalId = setInterval(() => {
      this.matchIncidentsService.refreshMatchIncidents(this.matchId());
    }, 1000 * 60 * 5); // cada 5 minutos
  }

  getCompetitionLogo() {
    return this.competition()?.ImageUrl;
  }
  getCompetitionName() {
    if (!this.competition()) return '-';
    return this.translateService.getTraduccionName(
      this.competition()?.NameTranslation!,
      this.competition()?.Name!
    );
  }
  getTitle() {
    return this.translateServiceCore.instant('Highlights');
  }

  ngOnDestroy() {
    clearInterval(this.refreshIntervalId);
    this.wsIncidents.disconnect();
    this.soccerMatchesService.wsIncidents.set({});
  }
}
